import React, { useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useWebMapContext } from '../webMap/WebMapContext';
import type { WidgetDataBinding } from './types';

interface Props {
  dataBinding?: WidgetDataBinding;
  pageSize?: number;
  compact?: boolean;
  visibleColumns?: string[];
  columnLabels?: Record<string, string>;
}

const DataTableWidget: React.FC<Props> = ({
  dataBinding,
  pageSize = 25,
  compact,
  visibleColumns,
  columnLabels,
}) => {
  const ctx = useWebMapContext();
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');

  const features = useMemo(() => {
    if (!dataBinding?.boundLayerId || !ctx) return [];
    return ctx.getFeaturesForLayer(dataBinding.boundLayerId);
  }, [dataBinding?.boundLayerId, ctx]);

  const allColumns = useMemo(() => {
    if (!features.length) return [];
    const keySet = new Set<string>();
    for (const f of features.slice(0, 100)) {
      if (f.properties) Object.keys(f.properties).forEach((k) => keySet.add(k));
    }
    return Array.from(keySet).filter((k) => !k.startsWith('__') && k !== 'geometry');
  }, [features]);

  const columns = useMemo(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      return visibleColumns.filter((c) => allColumns.includes(c));
    }
    return allColumns;
  }, [allColumns, visibleColumns]);

  const searched = useMemo(() => {
    if (!search.trim()) return features;
    const q = search.toLowerCase();
    return features.filter((f) => {
      const props = f.properties;
      if (!props) return false;
      for (const col of columns) {
        const v = props[col];
        if (v != null && String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [features, columns, search]);

  const sorted = useMemo(() => {
    if (!sortField) return searched;
    return [...searched].sort((a, b) => {
      const va = a.properties?.[sortField] ?? '';
      const vb = b.properties?.[sortField] ?? '';
      const na = Number(va);
      const nb = Number(vb);
      if (Number.isFinite(na) && Number.isFinite(nb)) {
        return sortDir === 'asc' ? na - nb : nb - na;
      }
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [searched, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  if (!dataBinding?.boundLayerId) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Bind this widget to a Web Map layer to display feature data.
        </Typography>
      </Box>
    );
  }

  if (!features.length) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">No features loaded.</Typography>
      </Box>
    );
  }

  const cellPad = compact ? '4px 6px' : '6px 10px';
  const fontSize = compact ? 11 : 12;

  const handleSort = (col: string) => {
    if (sortField === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(col);
      setSortDir('asc');
    }
    setPage(0);
  };

  const displayLabel = (col: string) => columnLabels?.[col] || col;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Search bar */}
      <Box sx={{ px: 1, pt: 0.5, pb: 0.5, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
            sx: { fontSize: 12 },
          }}
        />
      </Box>

      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    p: cellPad,
                    fontSize,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    bgcolor: 'background.paper',
                    borderBottom: '2px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <TableSortLabel
                    active={sortField === col}
                    direction={sortField === col ? sortDir : 'asc'}
                    onClick={() => handleSort(col)}
                    sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
                  >
                    {displayLabel(col)}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((f, ri) => (
              <TableRow key={ri} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                {columns.map((col) => {
                  const raw = f.properties?.[col];
                  const display = raw == null ? '' : typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
                  return (
                    <TableCell
                      key={col}
                      sx={{ p: cellPad, fontSize, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {display}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer: row count + pagination */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {search.trim() ? `${sorted.length.toLocaleString()} / ${features.length.toLocaleString()}` : sorted.length.toLocaleString()} rows
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton size="small" disabled={safePage === 0} onClick={() => setPage((p) => p - 1)}>
            <PrevIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {safePage + 1} / {totalPages}
          </Typography>
          <IconButton size="small" disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            <NextIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default DataTableWidget;
